/**
 * Parse FAQ markdown string into Q&A pairs
 * Extracts questions from H3 headings (###) and answers from following text
 * @param {string} faqText - Markdown FAQ text
 * @returns {Array} Array of {question: string, answer: string} objects
 */
export const parseFaqFromMarkdown = (faqText) => {
  if (!faqText || typeof faqText !== 'string') return []
  
  // Convert escaped newlines to actual newlines
  let text = faqText.replace(/\\n/g, '\n').replace(/\\r/g, '\n')
  
  // Remove the main FAQ heading (## Häufig gestellte Fragen)
  text = text.replace(/^##\s+[^\n]+\n\n?/i, '')
  
  const lines = text.split('\n')
  const faqs = []
  let currentQuestion = null
  let currentAnswer = []
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()
    
    // Check if this is a question (H3 heading - ###)
    if (line.match(/^###\s+/)) {
      // Save previous Q&A if exists
      if (currentQuestion && currentAnswer.length > 0) {
        faqs.push({
          question: currentQuestion,
          answer: currentAnswer.join('\n').trim()
        })
      }
      // Start new question
      currentQuestion = line.replace(/^###\s+/, '').trim()
      currentAnswer = []
    } else if (currentQuestion) {
      // This is part of the answer
      if (line) {
        currentAnswer.push(line)
      } else if (currentAnswer.length > 0) {
        // Empty line in answer - preserve it
        currentAnswer.push('')
      }
    }
  }
  
  // Don't forget the last Q&A
  if (currentQuestion && currentAnswer.length > 0) {
    faqs.push({
      question: currentQuestion,
      answer: currentAnswer.join('\n').trim()
    })
  }
  
  return faqs
}

/**
 * Convert markdown-style text to HTML
 * Handles paragraphs, bold, italic, links, and lists (bullets and numbered)
 * Works even when content has no line breaks (single-line JSON strings)
 * Supports bullet points (*, -, +) and numbered lists (1., 2., etc.)
 */
export const markdownToHtml = (text) => {
  if (!text || typeof text !== 'string') return ''
  
  // Performance debugging
  const startTime = performance.now()
  
  let html = text
  
  // Convert JSON-escaped newlines to actual newlines
  html = html.replace(/\\n/g, '\n')
  html = html.replace(/\\r/g, '\n')
  html = html.replace(/\\t/g, ' ')
  
  // Convert markdown links to HTML (before other processing)
  // Internal links (starting with /) don't need target="_blank"
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
    if (url.startsWith('/')) {
      // Internal link - no target="_blank"
      return `<a href="${url}">${text}</a>`
    } else {
      // External link - open in new tab
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`
    }
  })
  
  // Handle bullet points and numbered lists BEFORE paragraph processing
  // Process lists line by line to properly group them
  const lines = html.split('\n')
  let processedLines = []
  let inBulletList = false
  let inNumberedList = false
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const bulletMatch = line.match(/^[*\-+]\s+(.+)$/)
    const numberMatch = line.match(/^\d+\.\s+(.+)$/)
    
    if (bulletMatch) {
      if (!inBulletList) {
        if (inNumberedList) {
          processedLines.push('</ol>')
          inNumberedList = false
        }
        processedLines.push('<ul>')
        inBulletList = true
      }
      processedLines.push(`<li>${bulletMatch[1]}</li>`)
    } else if (numberMatch) {
      if (!inNumberedList) {
        if (inBulletList) {
          processedLines.push('</ul>')
          inBulletList = false
        }
        processedLines.push('<ol>')
        inNumberedList = true
      }
      processedLines.push(`<li>${numberMatch[1]}</li>`)
    } else {
      if (inBulletList) {
        processedLines.push('</ul>')
        inBulletList = false
      }
      if (inNumberedList) {
        processedLines.push('</ol>')
        inNumberedList = false
      }
      processedLines.push(line)
    }
  }
  
  // Close any open lists
  if (inBulletList) processedLines.push('</ul>')
  if (inNumberedList) processedLines.push('</ol>')
  
  html = processedLines.join('\n')
  
  // Remove any remaining table syntax (legacy content) - convert to plain text
  html = html.replace(/\|([^|\n]+(?:\s*\|\s*[^|\n]+)*)\|/g, (match, content) => {
    // Replace pipes with spaces and clean up
    return content.trim().replace(/\s*\|\s*/g, ' ').trim()
  })
  
  // Clean up multiple consecutive newlines (normalize to double newlines for paragraphs)
  html = html.replace(/\n{3,}/g, '\n\n')
  
  // Process formatting (bold, italic) before splitting into paragraphs
  html = html.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
  html = html.replace(/\*([^*]+?)\*/g, '<em>$1</em>')
  html = html.replace(/_([^_]+?)_/g, '<em>$1</em>')
  
  // Split into paragraphs (double newlines), but preserve lists
  // Lists are already wrapped in <ul>/<ol>, so we need to handle them separately
  const parts = html.split(/\n\n+/)
  
  // Process each part
  html = parts.map(part => {
    part = part.trim()
    if (!part) return ''
    
    // If it's already a list (contains <ul> or <ol>), return as-is
    if (part.includes('<ul>') || part.includes('<ol>')) {
      return part
    }
    
    // If it contains list items but not wrapped, wrap them
    if (part.includes('<li>')) {
      // Already processed, return as-is
      return part
    }
    
    // Convert single newlines to <br> within paragraphs
    part = part.replace(/\n(?!\n)/g, '<br>')
    
    // Wrap in paragraph
    return `<p>${part}</p>`
  }).join('')
  
  // Performance debugging
  const endTime = performance.now()
  const duration = endTime - startTime
  if (duration > 10) { // Only log if it takes more than 10ms
    console.warn(`⚠️ markdownToHtml took ${duration.toFixed(2)}ms for ${text.length} chars`)
  }
  
  return html
}
